import { captureOAuthParams, isSsoRedirectPending } from '@asi-lda/pnet-auth-web';

import { clearSession } from '@/auth/clearSession';
import { forgetRoleBootstrap, preloadRole } from '@/auth/roleBoot';
import { removeLegacyAccessKeys, removeLegacySelectionKeys } from '@/auth/roleState';
import {
  type BootOutcome,
  isSignedIn,
  permissionsLoaded,
  setBootOutcome,
  setSessionUser,
  writeSessionData,
} from '@/auth/session';
import { isSigningOut } from '@/config/signOutState';
import { ApiProvider } from '@/provider/ApiProvider';
import { sessionKeys } from '@/query/keys';

/**
 * Loading the session on a page load (UI state plan phase 1, decision D2).
 *
 * The session lives in memory only, so a hard refresh (or a new tab) starts
 * without it. Before the app renders, the page load asks the identity service
 * who is signed in (its read-only `GET me/session` with this tab's token;
 * `POST LoginData` only where that route is not available, see
 * ApiProvider.requestSessionUser), then confirms
 * the active role and loads its data (auth/roleBoot.ts). The app's providers
 * and pages therefore mount with the same session they used to find in
 * browser storage. Cost: the session requests once per page load, none on
 * in-app navigation.
 *
 * The login flow is not touched: this runs only when the page load is not a
 * sign-in in progress (no OAuth callback, no pending SSO redirect), it only
 * READS the token through ApiProvider.getAuthToken(), and without a token
 * nothing is sent.
 */
// Captured at module load, before Amplify can clear the callback params (routes/index.tsx).
const oauthCallbackAtLoad = captureOAuthParams().hasCallbackParams;

/** A sign-in is in progress on this page load: the login flow owns the session. */
export const signInInProgress = (): boolean => {
  if (oauthCallbackAtLoad) return true;
  try {
    if (isSsoRedirectPending()) return true;
    return typeof window !== 'undefined' && window.location.hash.startsWith('#/sso');
  } catch {
    return false;
  }
};

/** Who is signed in. */
async function loadUser(): Promise<BootOutcome> {
  if (isSignedIn()) return 'signed-in';
  if (signInInProgress()) return 'signed-out';

  const answer = await ApiProvider.requestSessionUser();
  // a sign-in on this page finished while the request ran: its session wins
  if (isSignedIn()) return 'signed-in';
  if (answer.status === 'ok') {
    setSessionUser(answer.data.user);
    writeSessionData(sessionKeys.loginContext, answer.data.active_context ?? null);
    // the default saved filter: the header starts from it when the server keeps
    // no selection for the role (ApiProvider.loadHeaderSelection)
    writeSessionData(
      sessionKeys.defaultFilter,
      answer.data.default_filter?.[0]?.filters_comination ?? null,
    );
    return 'signed-in';
  }
  if (answer.status === 'unavailable') return 'unavailable';
  if (answer.status === 'refused') {
    // the token is there but the server refuses the session: as a 401 on any
    // call (role state cleared, `sessionurl` kept, notice on the login page)
    clearSession('expired');
    if (answer.httpStatus === 401) ApiProvider.noteSessionExpired();
  }
  return 'signed-out';
}

/** null = the page is reloading (the role was switched elsewhere): keep the loader. */
async function loadSession(): Promise<BootOutcome | null> {
  // one-off: access data and the header selection older builds kept in browser storage
  removeLegacyAccessKeys();
  removeLegacySelectionKeys();
  const user = await loadUser();
  if (user !== 'signed-in') return user;
  // the active role and its data, unless the user has to choose one (RoleGate asks)
  const role = await preloadRole();
  if (role === 'reloading') return null;
  // The role is confirmed but its permission map did not arrive (the gateway did
  // not answer, a server error, a rate limit). Rendering the app now would leave
  // every gated page blank and then on "No permission" (PageGate fails closed
  // after its wait), for a user who holds the permission. As for the user above:
  // not a refusal, so the retry screen. A session the server ended meanwhile
  // (401: signed out, the login page loads) is not touched.
  if (role === 'ready' && isSignedIn() && !isSigningOut() && !permissionsLoaded()) {
    return 'unavailable';
  }
  return 'signed-in';
}

let boot: Promise<BootOutcome | null> | null = null;

/** Once per page load (a retry after `unavailable` runs it again). */
export const bootSession = (): Promise<BootOutcome | null> => {
  boot ??= loadSession()
    .catch((): BootOutcome => 'unavailable')
    .then((result) => {
      setBootOutcome(result);
      return result;
    });
  return boot;
};

/**
 * After `unavailable`: load again what is missing (the user from the identity
 * service, or only the role's data when the user is already in memory).
 */
export const retryBootSession = (): Promise<BootOutcome | null> => {
  boot = null;
  forgetRoleBootstrap();
  setBootOutcome(null);
  return bootSession();
};

/** Test helper. */
export const resetSessionBootForTests = (): void => {
  boot = null;
  setBootOutcome(null);
};
